/**
 * 이미지 접근 제한 우회를 위한 하이브리드 전략 유틸리티
 *
 * 전략 1: 서버 fetch (백엔드에서 처리)
 * 전략 2: 클라이언트 Base64 변환 후 재시도 (Kakao CDN Signed URL 등 서버 fetch 불가 시)
 * 전략 3: 사용자 업로드 유도 (CORS 완전 차단 시)
 */

/**
 * 이미지 URL을 브라우저에서 직접 fetch하여 Base64 문자열로 변환합니다.
 * Kakao CDN Signed URL처럼 브라우저에서는 접근 가능하지만 서버에서는 403/404가 발생하는 경우에 사용합니다.
 *
 * @param url 변환할 이미지 URL
 * @returns Base64 문자열 (data: 접두사 없음, PNG 형식), 실패 시 null
 */
export async function convertImageToBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
    });

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();

    // 이미지가 아닌 경우 null 반환
    if (!blob.type.startsWith('image/')) {
      return null;
    }

    return await new Promise<string | null>((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(blob);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        // data:image/png;base64,... 에서 헤더 제거 후 순수 base64만 반환
        const dataUrl = canvas.toDataURL('image/png');
        const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
        resolve(base64);
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };

      img.src = objectUrl;
    });
  } catch {
    // CORS 차단 등으로 fetch 자체가 실패한 경우
    return null;
  }
}

/**
 * Base64 이미지를 지정된 최대 크기로 리사이즈합니다.
 * 큰 이미지를 서버로 전송 전에 압축하여 RequestTooLarge 에러를 방지합니다.
 *
 * @param base64 순수 Base64 문자열 (data: 접두사 없음)
 * @param maxWidth 최대 가로 픽셀 (기본: 1920)
 * @param maxHeight 최대 세로 픽셀 (기본: 1080)
 * @param quality JPEG 품질 (0~1, 기본: 0.85)
 * @returns 리사이즈된 Base64 문자열, 실패 시 원본 반환
 */
export async function resizeImageBase64(
  base64: string,
  maxWidth = 1920,
  maxHeight = 1080,
  quality = 0.85
): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      let { width, height } = img;

      // 비율 유지하여 축소
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(base64);
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(dataUrl.replace(/^data:image\/\w+;base64,/, ''));
    };

    img.onerror = () => resolve(base64);
    img.src = `data:image/png;base64,${base64}`;
  });
}

/**
 * 에러 응답이 이미지 접근 제한으로 인한 실패인지 판단합니다.
 * Base64 재시도를 수행해야 하는지 여부를 반환합니다.
 */
export function isImageAccessError(status: number, errorType?: string): boolean {
  // 백엔드가 구조화된 errorType을 반환하는 경우
  if (errorType === 'KAKAO_CDN_SIGNED_URL' || errorType === 'CDN_ACCESS_DENIED') {
    return true;
  }
  // 403 Forbidden, 404 Not Found는 접근 제한으로 간주
  return status === 403 || status === 404;
}

/**
 * Kakao CDN 보안 정책으로 인해 프록시 다운로드가 불가능할 때 throw됩니다.
 * 호출부에서 instanceof KakaoCDNBlockedError로 감지하여 업로드 안내를 표시하세요.
 */
export class KakaoCDNBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'KakaoCDNBlockedError';
  }
}

const safeParseJson = (text: string): any | null => {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

function shouldUseProxyFirst(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return (
      hostname.includes('daumcdn.net') ||
      hostname.includes('kakao') ||
      hostname.includes('cdn-nhncommerce.com')
    );
  } catch {
    return false;
  }
}

/**
 * 백엔드 이미지 프록시를 통해 이미지를 Base64로 변환합니다.
 * Kakao CDN, Daum CDN 등 CORS를 완전히 차단하는 경우에 사용합니다.
 * 브라우저 직접 fetch(convertImageToBase64)가 실패할 때 fallback으로 사용합니다.
 *
 * @param url 변환할 이미지 URL
 * @param apiBaseUrl API 기본 URL (예: http://localhost:8080)
 * @param referer 이미지 원본 페이지 URL (Kakao CDN allow_referer 우회용, 선택)
 * @returns Base64 문자열 (data: 접두사 없음), 실패 시 null
 * @throws KakaoCDNBlockedError 프록시가 403 반환 시 (Kakao CDN 보안 정책 차단)
 */
export async function convertImageToBase64ViaProxy(url: string, apiBaseUrl: string, referer?: string): Promise<string | null> {
  try {
    let proxyUrl = `${apiBaseUrl}/api/crawler/image-proxy?url=${encodeURIComponent(url)}`;
    if (referer) proxyUrl += `&referer=${encodeURIComponent(referer)}`;
    console.log('[Base64 프록시] 프록시 시도:', proxyUrl);

    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const headers: HeadersInit = {};
    if (token) (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    const response = await fetch(proxyUrl, { headers });

    if (!response.ok) {
      if (response.status === 403) {
        const bodyText = await response.text().catch(() => '');
        const parsed = safeParseJson(bodyText);
        const errorType = parsed?.errorType || parsed?.code || parsed?.error;
        const message = parsed?.message || bodyText || '이미지 프록시 요청이 차단되었습니다.';

        if (errorType === 'SSRF_BLOCKED') {
          console.warn('[Base64 프록시] SSRF 차단 응답 감지:', message);
          return null;
        }

        throw new KakaoCDNBlockedError(message);
      }
      console.warn('[Base64 프록시] 프록시 실패:', response.status);
      return null;
    }

    const blob = await response.blob();

    if (!blob.type.startsWith('image/')) {
      return null;
    }

    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        // data:image/...;base64, 헤더 제거 후 순수 base64만 반환
        const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
        console.log('[Base64 프록시] 변환 성공, 길이:', base64.length);
        resolve(base64);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    if (e instanceof KakaoCDNBlockedError) throw e; // 호출부로 전파
    console.warn('[Base64 프록시] 요청 실패:', e);
    return null;
  }
}

/**
 * 이미지 URL을 Base64로 변환하는 스마트 전략 함수입니다.
 * 1차: 브라우저 직접 fetch (일반 CDN)
 * 2차: 백엔드 프록시 (Kakao/Daum CDN 등 CORS 차단 CDN)
 *
 * @param url 변환할 이미지 URL
 * @param apiBaseUrl API 기본 URL (프록시 fallback용)
 * @param referer 이미지 원본 페이지 URL (Kakao CDN allow_referer 우회용, 선택)
 * @returns Base64 문자열 (data: 접두사 없음), 모두 실패 시 null
 * @throws KakaoCDNBlockedError 프록시가 403 반환 시
 */
export async function convertImageToBase64Smart(url: string, apiBaseUrl: string, referer?: string): Promise<string | null> {
  if (shouldUseProxyFirst(url)) {
    console.log('[Base64 스마트] CDN 패턴 감지 → 프록시 우선 시도');
    return await convertImageToBase64ViaProxy(url, apiBaseUrl, referer);
  }

  // 1차: 브라우저 직접 fetch
  const direct = await convertImageToBase64(url);
  if (direct) {
    console.log('[Base64 스마트] 직접 fetch 성공');
    return direct;
  }

  // 2차: Kakao/Daum CDN 감지 시 프록시 우선 시도 (CORS 완전 차단)
  console.log('[Base64 스마트] 직접 fetch 실패 → 프록시 시도');
  return await convertImageToBase64ViaProxy(url, apiBaseUrl, referer); // KakaoCDNBlockedError 전파됨
}
